import { API_URLS } from "@/config/urls";
import { handleAxiosError, isAxiosError } from "@/lib/apiErrors";
import { api } from "@/lib/axios";
import type {
  InvitableUser,
  LightStudyGroupResponse,
  PaginatedResponse,
  StudyGroupForFilter,
  StudyGroupMembershipResponse,
  StudyGroupResponse,
  StudyGroupUnreadTotals,
  StudyGroupYourMembershipResponse,
  TagResponse,
  User,
} from "@/lib/humhub-api/types";
import type { StudyGroupSearchParams } from "@/types";

export const leaveStudyGroup = async (studyGroupId: string | number) => {
  try {
    const response = await api.delete(
      `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/delete-myself`,
    );
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      handleAxiosError(
        error,
        "An error occurred while leaving the study group",
      );
    }
    throw new Error("Unexpected error occurred");
  }
};

export const archiveStudyGroup = async (studyGroupId: string | number) => {
  try {
    const response = await api.post(
      `${API_URLS.STUDY_GROUP}/${studyGroupId}/archive`,
    );
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      handleAxiosError(
        error,
        "An error occurred while archiving the study group",
      );
    }
    throw new Error("Unexpected error occurred");
  }
};

export const getStudyGroupUnreadCounts =
  async (): Promise<StudyGroupUnreadTotals> => {
    try {
      type StudyGroupUnreadCountsResponse = {
        unread_post_comment_count_total: number;
        unread_mention_count_total: number;
      };

      const response = await api.get<StudyGroupUnreadCountsResponse>(
        `${API_URLS.STUDY_GROUP}/unread-counts`,
      );

      return {
        unreadPostCommentCount:
          response.data.unread_post_comment_count_total ?? 0,
        unreadMentions: response.data.unread_mention_count_total ?? 0,
      };
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching unread study group counts",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  };

export const unarchiveStudyGroup = async (studyGroupId: string | number) => {
  try {
    const response = await api.post(
      `${API_URLS.STUDY_GROUP}/${studyGroupId}/unarchive`,
    );
    return response.data;
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      handleAxiosError(
        error,
        "An error occurred while unarchiving the study group",
      );
    }
    throw new Error("Unexpected error occurred");
  }
};

export const studyGroupsApi = {
  getAllStudyGroups: async (
    page = 1,
    perPage = 20,
    searchParams: StudyGroupSearchParams = {},
  ): Promise<PaginatedResponse<LightStudyGroupResponse>> => {
    try {
      const response = await api.get<
        PaginatedResponse<LightStudyGroupResponse>
      >(`${API_URLS.STUDY_GROUP}/light-index`, {
        params: {
          page,
          limit: perPage,
          ...searchParams,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching all study groups",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getMyStudyGroups: async (
    page = 1,
    perPage = 20,
    status?: string,
  ): Promise<PaginatedResponse<LightStudyGroupResponse>> => {
    try {
      const params: Record<string, string | number | undefined> = {
        page,
        limit: perPage,
      };

      // statusが指定されている場合のみクエリパラメータに追加
      if (status) {
        params.status = status;
      }

      const response = await api.get<
        PaginatedResponse<LightStudyGroupResponse>
      >(`${API_URLS.STUDY_GROUP}/light-find-by-current-user`, { params });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching your study groups",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getStudyGroupsByUserId: async (
    userId: string | number,
    sort = "newer",
    page = 1,
    perPage = 20,
  ): Promise<PaginatedResponse<LightStudyGroupResponse>> => {
    try {
      const response = await api.get<
        PaginatedResponse<LightStudyGroupResponse>
      >(`${API_URLS.STUDY_GROUP}/light-find-by-user-id/${userId}`, {
        params: {
          sort,
          page,
          limit: perPage,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching study groups by user ID",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getStudyGroupInfo: async (
    id: string | number,
  ): Promise<StudyGroupResponse> => {
    try {
      const response = await api.get<StudyGroupResponse>(
        `${API_URLS.STUDY_GROUP}/${id}/flex`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching study group info",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getStudyGroupInfoByGuid: async (
    guid: string,
  ): Promise<StudyGroupResponse> => {
    try {
      const response = await api.get<StudyGroupResponse>(
        `${API_URLS.STUDY_GROUP}/guid/${guid}`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching study group info by guid",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  createStudyGroup: async (studyGroupData: {
    name: string;
    description: string;
    about: string;
    visibility: number;
    join_policy: number;
    language: string;
    tags: string[];
    color: string;
    default_content_visibility: number;
    member_chat_enabled: boolean;
  }) => {
    try {
      const response = await api.post(
        `${API_URLS.STUDY_GROUP}`,
        studyGroupData,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while creating the study group",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  updateStudyGroup: async (
    studyGroupId: string | number,
    studyGroupData: {
      name: string;
      description: string;
      about: string;
      visibility: number;
      join_policy: number;
      language: string;
      tags: string[];
      color: string;
      default_content_visibility: number;
      member_chat_enabled: boolean;
    },
  ) => {
    try {
      const response = await api.put(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}`,
        studyGroupData,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while updating the study group",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getAvailableStudyGroupTags: async (
    searchParams: StudyGroupSearchParams = {},
  ): Promise<string[]> => {
    try {
      const response = await api.get<PaginatedResponse<TagResponse>>(
        `${API_URLS.TAG}/available-space-tags`,
        {
          params: {
            ...searchParams,
          },
        },
      );

      return response.data.results.map((tag: TagResponse) => tag.name);
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching available study group tags",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getStudyGroupMemberships: async (
    id: string | number,
    perPage = 20,
    page = 1,
    keyword?: string,
    membershipStatus?: number,
  ): Promise<PaginatedResponse<StudyGroupMembershipResponse>> => {
    try {
      const response = await api.get<
        PaginatedResponse<StudyGroupMembershipResponse>
      >(`${API_URLS.STUDY_GROUP}/${id}/membership/find-by-membership-status`, {
        params: {
          limit: perPage,
          page: page,
          keyword: keyword,
          membershipStatus: membershipStatus,
        },
      });
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching study group memberships",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getStudyGroupYourMembership: async (
    studyGroupId: string | number,
  ): Promise<StudyGroupYourMembershipResponse> => {
    try {
      const response = await api.get<StudyGroupYourMembershipResponse>(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/current-user`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching your study group membership",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getStudyGroupsForFilter: async (
    keyword?: string,
  ): Promise<PaginatedResponse<StudyGroupForFilter[]>> => {
    try {
      const params = keyword ? { keyword } : {};
      const response = await api.get<PaginatedResponse<StudyGroupForFilter[]>>(
        `${API_URLS.STUDY_GROUP}/space-for-filter`,
        { params },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching study groups for filter",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  join: async (studyGroupId: string | number) => {
    try {
      const response = await api.post(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/join`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while joining the study group",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  updateLastVisit: async (studyGroupId: string | number) => {
    try {
      const response = await api.put(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/visit`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while updating last visit");
      }
      throw new Error("Unexpected error occurred");
    }
  },
  forceJoin: async (studyGroupId: string | number) => {
    try {
      const response = await api.post(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/force-join`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while force joining the study group",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  applyMembership: async (studyGroupId: string | number) => {
    try {
      const response = await api.post(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/apply`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while applying for membership",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  revokeMembership: async (studyGroupId: string | number) => {
    try {
      const response = await api.delete(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/revoke`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while revoking membership");
      }
      throw new Error("Unexpected error occurred");
    }
  },
  batchRevokeMembership: async (
    studyGroupId: string | number,
    userIds?: string | number | (string | number)[],
  ) => {
    try {
      const response = await api.delete(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/batch-revoke`,
        {
          data: {
            userIds: userIds
              ? Array.isArray(userIds)
                ? userIds
                : [userIds]
              : undefined,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(error, "An error occurred while revoking membership");
      }
      throw new Error("Unexpected error occurred");
    }
  },
  acceptInvitation: async (studyGroupId: string | number) => {
    try {
      const response = await api.put(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/accept-invitation`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while accepting the invitation",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  batchRevokeInvitation: async (
    studyGroupId: string | number,
    userIds: string | number | (string | number)[],
  ) => {
    try {
      const response = await api.delete(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/batch-revoke-invitation`,
        {
          data: {
            userIds: Array.isArray(userIds) ? userIds : [userIds],
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while revoking the invitation",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  batchAcceptApplication: async (
    studyGroupId: string | number,
    userIds: string | number | (string | number)[],
  ) => {
    try {
      const response = await api.put(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/batch-accept-application`,
        {
          userIds: Array.isArray(userIds) ? userIds : [userIds],
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while accepting the application",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  uploadStudyGroupImage: async (
    studyGroupId: string | number,
    profileImage: File,
  ): Promise<void> => {
    try {
      const formData = new FormData();
      formData.append("profileImage", profileImage);

      await api.post(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/upload-profile-image`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while uploading the study group image",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  leaveStudyGroup,
  inviteUserToStudyGroup: async (
    studyGroupId: string | number,
    userIds: (string | number)[],
    options?: {
      sendNotification?: boolean;
      message?: string;
      language?: string;
    },
  ) => {
    try {
      const { sendNotification = true, message, language } = options ?? {};
      const response = await api.post(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/invite`,
        {
          userIds,
          sendNotification,
          message,
          language,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while inviting users to the study group",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  inviteUserByEmailToStudyGroup: async (
    studyGroupId: string | number,
    emails: string[],
    sendNotification = true,
    message?: string,
    language?: string,
  ) => {
    try {
      const response = await api.post(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/invite-by-email`,
        {
          emails,
          sendNotification,
          message,
          language,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        // 400エラーでresults構造を持つ場合は、詳細な失敗情報を含むレスポンスを返す
        if (
          error.response?.status === 400 &&
          error.response?.data &&
          typeof error.response.data === "object" &&
          "results" in error.response.data
        ) {
          return error.response.data;
        }
        handleAxiosError(
          error,
          "An error occurred while inviting users to the study group",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getInvitableUsers: async (
    studyGroupId: string | number,
    keyword?: string,
  ): Promise<PaginatedResponse<InvitableUser>> => {
    try {
      const response = await api.get(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/invitable-users`,
        {
          params: keyword ? { keyword } : {},
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching invitable users",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getMentionAvailableUsers: async (
    studyGroupId: string | number,
    keyword?: string,
  ): Promise<User[]> => {
    try {
      const response = await api.get(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/filter-mention-available-users`,
        {
          params: keyword ? { keyword } : {},
        },
      );
      interface MentionUserItem {
        user: User;
      }
      return (
        response.data.results?.map((item: MentionUserItem) => item.user) || []
      );
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching mention available users",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  updateMemberRole: async (
    studyGroupId: string | number,
    userId: string | number,
    role: "admin" | "moderator" | "member",
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await api.put(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/${userId}/role`,
        { role },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while updating the member role",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
  getAdmins: async (
    studyGroupId: string | number,
    perPage = 20,
    page = 1,
  ): Promise<PaginatedResponse<StudyGroupMembershipResponse>> => {
    try {
      const response = await api.get(
        `${API_URLS.STUDY_GROUP}/${studyGroupId}/membership/admins`,
        {
          params: {
            limit: perPage,
            page: page,
          },
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        handleAxiosError(
          error,
          "An error occurred while fetching the study group admins",
        );
      }
      throw new Error("Unexpected error occurred");
    }
  },
};
